import { drillLabel, drillOf, type Drill } from './drill';
import { FUNNEL_EXPLAINERS } from './pipelineFunnel';
import type { PipelineRow } from './leadPipeline';
import type { PlannedItem } from './pipelineActions';

/**
 * Turning what the Pipeline panel already resolved into a page you can open.
 *
 * ── Why this derives from the panel rather than beside it ──────────────────
 *
 * `panelFor()` in PipelineView already maps every selection to the rows behind
 * it, and has since the funnel replaced the seven stacked bands. Writing a
 * second function that maps the same selections to the same leads would put
 * two definitions of "Emailed" in the codebase, and the whole argument for
 * pipelineFunnel.ts is that there should be one.
 *
 * So this takes the panel's own output. Whatever the panel is showing is what
 * the page opens. They cannot drift, because there is nothing to drift from.
 *
 * ── When there is no page to open ──────────────────────────────────────────
 *
 * Planned, due today and overdue list WORK — a task or a round touch, with a
 * due date and an owner. The Leads tab cannot show that; it shows leads. Those
 * selections return null and keep the inline panel, which is the only place
 * that view exists.
 *
 * The rule, stated once: if a page already lists this kind of record, go to
 * it. If none does, show it here.
 */

/** What the panel resolved, which is all this needs. */
export interface PanelResult {
  title: string;
  note: string;
  rows: PipelineRow[];
  items?: PlannedItem[];
}

/**
 * The drill for a panel, or null when the records have no page of their own.
 *
 * `slice` is the window and filter wording already printed at the top of the
 * Pipeline ("Outbound · last week"), so the banner on the Leads tab says which
 * slice the set was taken from. Without it "Emailed · 2" is ambiguous the
 * moment somebody changes the range control.
 */
export function drillForPanel(panel: PanelResult, slice: string, why: string): Drill | null {
  if (panel.items !== undefined) {
    return null;
  }
  return drillOf({
    label: drillLabel(panel.title, slice),
    why,
    kind: 'lead',
    ids: panel.rows.map((row) => row.leadId),
  });
}

/**
 * The written definition for a funnel step, or empty for everything else.
 *
 * FUNNEL_EXPLAINERS lives next to the arithmetic in pipelineFunnel.ts so the
 * sentence and the sum cannot drift. This reads it rather than restating it,
 * and returns empty rather than inventing a sentence for the selections that
 * have never had one — an explainer nobody wrote is worth seeing as absent.
 */
export function whyFor(key: string): string {
  return key in FUNNEL_EXPLAINERS ? FUNNEL_EXPLAINERS[key as keyof typeof FUNNEL_EXPLAINERS] : '';
}
